import { Link } from "react-router-dom";
import { ArrowRight, Coins, HeartHandshake, Flame, HeartPulse, Brain, GraduationCap } from "lucide-react";
import { PageHero, SectionLabel } from "@/components/Section";
import { IMAGES } from "@/data/content";
import { FoundationLink } from "@/components/FoundationLink";

export default function Cause() {
  return (
    <div data-testid="cause-page">
      <PageHero
        overline="The Cause"
        title="Supporting Our"
        accent="First Responders"
        subtitle="Providing meaningful support for the firefighters, law-enforcement officers, EMS, and dispatchers who protect our community and answer the call every day."
        image={IMAGES.firefighterSmoke}
      />

      <section className="paper-noise bg-background">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-center relative z-10">
          <div className="lg:col-span-6">
            <SectionLabel>The Mission</SectionLabel>
            <h2 className="mt-5 font-serif font-black text-4xl sm:text-5xl text-espresso leading-tight">
              Support For Those Who Serve
            </h2>
            <div className="mt-6 space-y-5 text-base md:text-lg text-muted-foreground leading-relaxed">
              <p>
                First responders give everything to keep our community safe — often at great personal cost. When disaster strikes, when injury or illness hits home, or when the weight of the job becomes too much to carry alone, they deserve a community that stands behind them.
              </p>
              <p>
                That's why we're here. Proceeds provide financial assistance for line-of-duty death or injury, property loss from natural disaster or fire, major medical expenses, cancer support, behavioral health, and educational scholarships — for <span className="text-espresso font-semibold">ALL</span> first responders who serve and protect us.
              </p>
              <p className="text-sm">
                Every dollar raised at the Heroes & Legacy Golf Classic goes directly toward this mission through the{" "}
                <FoundationLink testId="cause-foundation-site-link" className="text-espresso font-semibold underline decoration-rust/60 underline-offset-2 hover:text-rust transition-colors" icon={false}>
                  Wayne Walker Foundation
                </FoundationLink>.
              </p>
            </div>
          </div>
          <div className="lg:col-span-6 border border-border hard-shadow overflow-hidden">
            <img src={IMAGES.fireStation} alt="Fire station" className="w-full h-96 object-cover" />
          </div>
        </div>
      </section>

      <section className="bg-card paper-noise">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28 relative z-10">
          <SectionLabel>Where Your Support Goes</SectionLabel>
          <h2 className="mt-5 font-serif font-black text-4xl text-espresso">Real Impact, Locally</h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Coins, title: "Financial Hardship Assistance", desc: "Immediate funds for first responders and their families facing unexpected financial hardship in times of need." },
              { icon: HeartHandshake, title: "Line-of-Duty Death & Injury", desc: "Direct financial support for responders and their families in the event of a line-of-duty death or injury." },
              { icon: Flame, title: "Disaster & Fire Property Loss", desc: "Relief for members who suffer property loss due to natural disasters or fire." },
              { icon: HeartPulse, title: "Cancer Support", desc: "Assistance and advocacy for the rising number of responders diagnosed with cancer from on-the-job carcinogen exposure." },
              { icon: Brain, title: "Behavioral Health & Suicide Prevention", desc: "A key priority — mental-health support and ending the stigma, because more responders are lost to suicide than to line-of-duty deaths." },
              { icon: GraduationCap, title: "Educational Scholarships", desc: "Scholarships for qualifying dependents and community members pursuing careers in law enforcement, fire, EMS, or dispatch." },            ].map((c) => (
              <div key={c.title} className="border border-border bg-background p-7 transition-transform hover:-translate-y-1 hard-shadow-sm">
                <span className="h-11 w-11 bg-rust text-primary-foreground flex items-center justify-center"><c.icon size={20} /></span>
                <h3 className="mt-4 font-serif font-bold text-xl text-espresso">{c.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative bg-espresso text-cream overflow-hidden">
        <img src={IMAGES.firefighterPortrait} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-espresso to-espresso/60" />
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8 py-20 md:py-24 text-center">
          <h2 className="font-serif font-black text-3xl sm:text-4xl">Every Swing Supports A Hero</h2>
          <p className="mt-5 text-cream/80 text-base md:text-lg">Register your team or sponsor a tier — 100% in service of our first responders.</p>
          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <Link to="/register" className="label-font text-sm bg-rust text-primary-foreground px-8 py-4 border border-cream/20 transition-transform hover:-translate-y-1 hard-shadow flex items-center gap-2">Register a Team <ArrowRight size={16} /></Link>
            <Link to="/sponsor" className="label-font text-sm border border-cream/40 px-8 py-4 transition-colors hover:bg-cream/10">Become a Sponsor</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
